import React from 'react';
import styles from './FilterDateRange.module.scss';
import { SFDatePicker, SFSwitch } from 'sfui';
import moment from 'moment';
import { DateRange } from '../../../../../Models';

interface DateRangeError {
  dateFrom: boolean;
  dateTo: boolean;
}

const dateToday: moment.Moment = moment().endOf('day');

const isDateRangeError = (
  dateRange: DateRange,
  minDate?: moment.Moment
): DateRangeError => {
  const dateFrom: moment.Moment | undefined = dateRange.dateFrom
    ? moment(dateRange.dateFrom)
    : undefined;
  const dateTo: moment.Moment | undefined = dateRange.dateTo
    ? moment(dateRange.dateTo)
    : undefined;

  let dateFromError: boolean = !moment(dateRange.dateFrom).isValid();
  let dateToError: boolean = !moment(dateRange.dateTo).isValid();

  if (dateFrom) {
    if (minDate) {
      if (dateFrom < minDate || dateFrom > dateToday) {
        dateFromError = true;
      }
    } else {
      if (dateFrom > dateToday) {
        dateFromError = true;
      }
    }

    if (dateTo) {
      if (dateTo < dateFrom || dateTo > dateToday) {
        dateToError = true;
      }

      if (dateTo.diff(dateFrom, 'days') > 365) {
        dateToError = true;
        dateFromError = true;
      }
    } else if (!dateTo && !dateRange.exactDate) {
      dateToError = true;
    }
  }

  if (dateTo && !dateFrom) {
    dateFromError = true;
    dateToError = true;
  }

  return { dateFrom: dateFromError, dateTo: dateToError };
};

const EMPTY_FIELD_VALUE: DateRange = {
  dateFrom: undefined,
  dateTo: undefined,
  exactDate: false
};

export interface FilterDateRangeProps {
  minDate?: moment.Moment;
  onChange: (value: DateRange, hasError: boolean) => void;
  value: DateRange;
}

export const FilterDateRange = (
  props: FilterDateRangeProps
): React.ReactElement<FilterDateRangeProps> => {
  const value: DateRange = (props.value as DateRange) ?? EMPTY_FIELD_VALUE;
  const hasValueErrors = isDateRangeError(value, props.minDate);

  const onDateFromChange = (date: string | null | undefined) => {
    const newDateRange = {
      ...value,
      dateFrom: date ?? undefined
    };
    const hasError = isDateRangeError(newDateRange, props.minDate);
    props.onChange(newDateRange, hasError.dateFrom || hasError.dateTo);
  };

  const onDateToChange = (date: string | null | undefined) => {
    const newDateRange = {
      ...value,
      dateTo: date ?? undefined
    };
    const hasError = isDateRangeError(newDateRange, props.minDate);
    props.onChange(newDateRange, hasError.dateFrom || hasError.dateTo);
  };

  const onExactDateChange = (checked: boolean) => {
    const newDateRange = {
      ...value,
      dateTo: undefined,
      exactDate: checked
    };
    const hasError = isDateRangeError(newDateRange, props.minDate);
    props.onChange(newDateRange, hasError.dateFrom || hasError.dateTo);
  };

  return (
    <div className={styles.filterDateRange}>
      <SFDatePicker
        disableFuture
        label={value.exactDate ? 'On' : 'From'}
        minDate={props.minDate}
        maxDate={moment()}
        error={hasValueErrors.dateFrom}
        helperText={
          hasValueErrors.dateFrom ? 'Please enter a valid date.' : undefined
        }
        value={value.dateFrom}
        onChange={(_date, value) => onDateFromChange(value)}
      />
      {!value.exactDate && (
        <SFDatePicker
          disableFuture
          label="To"
          minDate={props.minDate}
          maxDate={moment()}
          error={hasValueErrors.dateTo}
          helperText={
            hasValueErrors.dateTo ? 'Please enter a valid date.' : undefined
          }
          value={value.dateTo}
          onChange={(_date, value) => onDateToChange(value)}
        />
      )}
      <SFSwitch
        label="Exact Date"
        checked={value.exactDate}
        onChange={(_e, checked: boolean) => onExactDateChange(checked)}
      />
    </div>
  );
};
